import { useState, useEffect } from "react";
import DashboardLayout from "../../components/layout/DashboardLayout";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import { toast } from "sonner";
import {
  Plus,
  Copy,
  ExternalLink,
  Trash2,
  Edit,
  Link2,
  MousePointer,
  Users,
  DollarSign,
  QrCode,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AffiliateLinks() {
  const { getAuthHeaders } = useAuth();
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingLink, setEditingLink] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    destination_path: "/signup",
    utm_source: "",
    utm_campaign: "",
    utm_medium: "",
    utm_content: "",
  });
  const [settings, setSettings] = useState({ signup_base_url: "https://maaly.io/signup" });

  useEffect(() => {
    fetchLinks();
    fetchSettings();
  }, []);

  const fetchLinks = async () => {
    try {
      const response = await fetch(`${API_URL}/api/affiliate/referral-links`, {
        credentials: "include",
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        setLinks(await response.json());
      }
    } catch (error) {
      toast.error("Failed to load referral links");
    } finally {
      setLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await fetch(`${API_URL}/api/settings/public`, {
        credentials: "include",
      });
      if (response.ok) {
        setSettings(await response.json());
      }
    } catch (error) {
      // Use defaults
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const url = editingLink
        ? `${API_URL}/api/affiliate/referral-links/${editingLink.id}`
        : `${API_URL}/api/affiliate/referral-links`;
      
      const response = await fetch(url, {
        method: editingLink ? "PATCH" : "POST",
        credentials: "include",
        headers: {
          ...getAuthHeaders(),
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        toast.success(editingLink ? "Link updated!" : "Link created!");
        fetchLinks();
        setDialogOpen(false);
        resetForm();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to save link");
      }
    } catch (error) {
      toast.error("Failed to save link");
    }
  };

  const handleDelete = async (linkId) => {
    if (!window.confirm("Are you sure you want to delete this link?")) return;

    try {
      const response = await fetch(`${API_URL}/api/affiliate/referral-links/${linkId}`, {
        method: "DELETE",
        credentials: "include",
        headers: getAuthHeaders(),
      });

      if (response.ok) {
        toast.success("Link deleted");
        fetchLinks();
      } else {
        toast.error("Failed to delete link");
      }
    } catch (error) {
      toast.error("Failed to delete link");
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      destination_path: "/signup",
      utm_source: "",
      utm_campaign: "",
      utm_medium: "",
      utm_content: "",
    });
    setEditingLink(null);
  };

  const openEditDialog = (link) => {
    setEditingLink(link);
    setFormData({
      name: link.name,
      destination_path: link.destination_path,
      utm_source: link.utm_source || "",
      utm_campaign: link.utm_campaign || "",
      utm_medium: link.utm_medium || "",
      utm_content: link.utm_content || "",
    });
    setDialogOpen(true);
  };

  const buildReferralUrl = (link) => {
    const baseUrl = settings.signup_base_url || "https://maaly.io/signup";
    let url = `${baseUrl}?ref=${link.code}`;
    
    if (link.utm_source) url += `&utm_source=${link.utm_source}`;
    if (link.utm_campaign) url += `&utm_campaign=${link.utm_campaign}`;
    if (link.utm_medium) url += `&utm_medium=${link.utm_medium}`;
    if (link.utm_content) url += `&utm_content=${link.utm_content}`;
    
    return url;
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    }).format(value || 0);
  };

  if (loading) {
    return (
      <DashboardLayout title="Referral Links" subtitle="Manage your referral campaigns">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Referral Links" subtitle="Manage your referral campaigns">
      <div data-testid="affiliate-links" className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <p className="text-muted-foreground">
            {links.length} referral link{links.length !== 1 ? "s" : ""}
          </p>
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button data-testid="create-link-btn" className="glow-primary">
                <Plus className="w-4 h-4 mr-2" /> Create Link
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-border">
              <DialogHeader>
                <DialogTitle className="text-foreground">
                  {editingLink ? "Edit Referral Link" : "Create Referral Link"}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Campaign Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Twitter Campaign Q1"
                    required
                    data-testid="link-name-input"
                    className="bg-background border-border text-foreground"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Destination Path</Label>
                  <Input
                    value={formData.destination_path}
                    onChange={(e) => setFormData({ ...formData, destination_path: e.target.value })}
                    placeholder="/signup"
                    data-testid="link-path-input"
                    className="bg-background border-border text-foreground"
                  />
                </div>

                <div className="border-t border-border pt-4">
                  <p className="text-sm text-muted-foreground mb-4">UTM Parameters (Optional)</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-muted-foreground">Source</Label>
                      <Input
                        value={formData.utm_source}
                        onChange={(e) => setFormData({ ...formData, utm_source: e.target.value })}
                        placeholder="twitter"
                        className="bg-background border-border text-foreground"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-muted-foreground">Campaign</Label>
                      <Input
                        value={formData.utm_campaign}
                        onChange={(e) => setFormData({ ...formData, utm_campaign: e.target.value })}
                        placeholder="launch"
                        className="bg-background border-border text-foreground"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-muted-foreground">Medium</Label>
                      <Input
                        value={formData.utm_medium}
                        onChange={(e) => setFormData({ ...formData, utm_medium: e.target.value })}
                        placeholder="social"
                        className="bg-background border-border text-foreground"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-muted-foreground">Content</Label>
                      <Input
                        value={formData.utm_content}
                        onChange={(e) => setFormData({ ...formData, utm_content: e.target.value })}
                        placeholder="banner"
                        className="bg-background border-border text-foreground"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1">
                    Cancel
                  </Button>
                  <Button type="submit" data-testid="save-link-btn" className="flex-1 glow-primary">
                    {editingLink ? "Update" : "Create"} Link
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Links List */}
        {links.length === 0 ? (
          <div className="bg-card border border-border rounded-xl p-12 text-center">
            <Link2 className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-foreground mb-2">No referral links yet</h3>
            <p className="text-muted-foreground mb-6">Create your first referral link to start tracking conversions</p>
            <Button onClick={() => setDialogOpen(true)} className="glow-primary">
              <Plus className="w-4 h-4 mr-2" /> Create Your First Link
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {links.map((link) => {
              const referralUrl = buildReferralUrl(link);
              return (
                <div
                  key={link.id}
                  className="bg-card border border-border rounded-xl p-6 hover:border-border transition-all"
                >
                  <div className="flex flex-col lg:flex-row lg:items-center gap-6">
                    {/* Link Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-semibold text-foreground">{link.name}</h3>
                        <span className="px-2 py-0.5 rounded text-xs font-mono bg-muted text-muted-foreground">
                          {link.code}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 p-2 rounded-lg bg-background border border-border">
                        <span className="flex-1 text-sm text-muted-foreground truncate font-mono">
                          {referralUrl}
                        </span>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => copyToClipboard(referralUrl)}
                          data-testid={`copy-link-${link.id}`}
                          className="flex-shrink-0"
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                        <a
                          href={referralUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-shrink-0 p-2 hover:bg-muted rounded-lg"
                        >
                          <ExternalLink className="w-4 h-4 text-muted-foreground" />
                        </a>
                      </div>
                    </div>

                    {/* Stats */}
                    <div className="flex gap-6 flex-shrink-0">
                      <div className="text-center">
                        <div className="flex items-center gap-1.5 text-muted-foreground mb-1">
                          <MousePointer className="w-4 h-4" />
                          <span className="text-xs">Clicks</span>
                        </div>
                        <p className="text-lg font-semibold text-foreground">{link.clicks || 0}</p>
                      </div>
                      <div className="text-center">
                        <div className="flex items-center gap-1.5 text-muted-foreground mb-1">
                          <Users className="w-4 h-4" />
                          <span className="text-xs">Signups</span>
                        </div>
                        <p className="text-lg font-semibold text-foreground">{link.signups || 0}</p>
                      </div>
                      <div className="text-center">
                        <div className="flex items-center gap-1.5 text-muted-foreground mb-1">
                          <DollarSign className="w-4 h-4" />
                          <span className="text-xs">Earnings</span>
                        </div>
                        <p className="text-lg font-semibold text-emerald-400">
                          {formatCurrency(link.total_earnings)}
                        </p>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2 flex-shrink-0">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openEditDialog(link)}
                        data-testid={`edit-link-${link.id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(link.id)}
                        data-testid={`delete-link-${link.id}`}
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
